export interface MeResponse {
  authenticated: boolean;
  user?: { name: string; email: string; roles: string[]; permissions?: string[] };
  csrf_token?: string;
}

export interface Ticket {
  id: string;
  tenant_id: string;
  opened_by: string;
  module: string;
  description: string;
  severity: string;
  status: string;
  opened_at: string;
  clinical_data_access: boolean;
}

export interface Grant {
  id: string;
  ticket_id: string;
  support_user_id: string;
  granted_at: string;
  expires_at: string;
  scope_modules: string[];
  scope_actions: string[];
  clinical_data_access: boolean;
  status: string;
}

export interface SupportActionRow {
  id: string;
  ticket_id: string;
  support_user_id: string;
  action: string;
  resource_type: string;
  resource_id: string;
  executed_at: string;
}

// Una versión del grant en su historial (platform.support_grant_change).
export interface GrantChange {
  id: string;
  seq: number;
  change_type: 'modify' | 'extend' | 'revoke';
  changed_by: string;
  before_value: Record<string, unknown>;
  after_value: Record<string, unknown>;
  reason: string;
  changed_at: string;
}

export interface Plan {
  id: string;
  code: string;
  name: string;
  modules: string[];
  max_branches: number;
  max_users: number;
  availability_tier: string;
  monthly_price_gtq: number;
}

export interface TenantView {
  id: string;
  slug: string;
  schema_name: string;
  active: boolean;
  features: Record<string, boolean>;
  plan_code: string;
  plan_name: string;
  license_status: string;
  availability_tier: string;
  brand_name: string;
  branch_count: number;
  // REQ-20: módulos del plan (base) + overrides crudos del tenant, para el editor de features.
  plan_modules?: string[];
  module_overrides?: Record<string, boolean>;
}

// Branding editable de un tenant (white-label). Espejo de platform.tenant_branding.
export interface TenantBranding {
  brand_name: string;
  primary_hex: string;
  logo_ref: string;
}

// Respuesta de PATCH .../license: features re-materializadas tras cambiar de plan.
export interface LicenseResult {
  tenant_id: string;
  plan_code: string;
  features: Record<string, boolean>;
}

// Sucursal del schema del tenant (GET .../tenants/{id}/branches).
export interface TenantBranchRow {
  id: string;
  name: string;
  address: string;
  timezone: string;
  active: boolean;
}

// Usuario proyectado del tenant (GET .../tenants/{id}/users).
export interface TenantUserRow {
  user_sub: string;
  branch_id: string;
}

// Conteos de actividad del tenant (GET .../tenants/{id}/usage).
export interface TenantUsage {
  tenant_id: string;
  slug: string;
  usage: {
    patients: number;
    invoices: number;
    open_tickets: number;
    active_grants: number;
  };
}

// --- Gestión de roles de PLATAFORMA (realm operations) ---

// Capability del catálogo global (platform.permission) para el picker de permisos.
export interface PlatformPermission {
  permission_key: string;
  module: string;
  action: string;
  description: string;
  clinical: boolean;
}

// Rol de plataforma (platform.app_role) con sus permisos.
export interface PlatformRole {
  id: string;
  codename: string;
  display_name: string;
  system: boolean;
  rank: number;
  created_by?: string;
  permissions: string[];
}

// has comprueba un permiso del usuario actual (RBAC del realm operations).
export function has(me: MeResponse | undefined, perm: string): boolean {
  const perms = me?.user?.permissions ?? [];
  if (perms.includes('*')) return true;
  if (perms.includes(perm)) return true;
  const mod = perm.split(':')[0];
  return perms.includes(`${mod}:*`);
}
